// src/pages/ConfiguracoesUsuario.jsx
import React, { useEffect, useRef, useState } from "react";
import styled from "styled-components";
import {
  LuUser, LuMail, LuCamera, LuKeyRound, LuExternalLink,
  LuBell, LuShield, LuTrash2
} from "react-icons/lu";
import { useNavigate } from "react-router-dom";

import { app } from "../firebase";
import LoadingComLayout from "../components/LoadingComLayout";
import { useToast } from "../components/ToastProvider";
import {
  getAuth,
  onAuthStateChanged,
  updateProfile,
  sendPasswordResetEmail,
  deleteUser,
  reload,
  fetchSignInMethodsForEmail,
} from "firebase/auth";
import {
  getFirestore, doc, getDoc, setDoc,
  collection, query, where, getDocs, limit,
  collectionGroup, documentId
} from "firebase/firestore";
import {
  getStorage, ref, uploadBytes, getDownloadURL
} from "firebase/storage";

const auth = getAuth(app);
const db = getFirestore(app);
const storage = getStorage(app);

/** ===== Feature flags ===== */
const NOTIFS_ENABLED = false;

/* ======= UI ======= */
const Main = styled.main`
  flex: 1;
  padding: 2rem 4rem;
  background: #f3f7ff;
  min-height: 100vh;
  display: flex; flex-direction: column; gap: 1.25rem;

  @media (max-width: 1280px){ padding: 2rem; }
  @media (max-width: 768px){ padding: 1rem; }
`;
const Title = styled.h2`
  font-size: 1.5rem; font-weight: 700; color: #1f1a40; margin: .25rem 0 1rem;
`;
const Grid = styled.div`
  display: grid; gap: 1rem; grid-template-columns: 2fr 1fr;
  @media (max-width: 1024px){ grid-template-columns: 1fr; }
`;
const Card = styled.section`
  background: #fff; border: 1px solid #e6ecf5; border-radius: 1rem;
  box-shadow: 0 6px 20px rgba(58,121,237,.06);
  position: relative;
`;
const CardHeader = styled.div`
  display:flex; align-items:center; gap:.6rem;
  padding: 1rem 1.1rem; border-bottom: 1px solid #eef2fb; color: #1f1a40; font-weight: 700;
  svg{ color:#3a79ed; }
`;
const CardBody = styled.div`
  padding: 1rem 1.1rem; display:flex; flex-direction:column; gap:.9rem;
`;
const Row = styled.div`
  display:grid; gap:.75rem; grid-template-columns: 160px 1fr;
  align-items:center;
  @media (max-width: 640px){ grid-template-columns: 1fr; align-items:stretch; }
`;
const Label = styled.label`
  color:#334155; font-weight:600; font-size:.95rem;
`;
const Input = styled.input`
  width:100%; padding:.75rem .9rem; border:1px solid #dfe6f6; border-radius:.75rem;
  background:#f8fbff; font-size:.95rem; outline:none;
  &:focus{ border-color:#3a79ed; box-shadow: 0 0 0 3px rgba(58,121,237,.15); }
  &:disabled{ opacity:.65; cursor:not-allowed; }
`;
const Button = styled.button`
  align-self:flex-start;
  display:inline-flex; align-items:center; gap:.6rem;
  padding:.75rem 1rem; font-weight:800; font-size:.95rem;
  color:#fff; background:#3a79ed; border:0; border-radius:.75rem;
  box-shadow:0 4px 12px rgba(58,121,237,.28);
  cursor:pointer; transition: transform .15s ease, box-shadow .15s ease, opacity .2s ease;
  &:hover{ transform: translateY(-1px); box-shadow:0 6px 16px rgba(58,121,237,.34); }
  &:disabled{ opacity:.6; cursor:not-allowed; }
`;
const Secondary = styled(Button)`
  background:#1f1a40; box-shadow:0 4px 12px rgba(31,26,64,.2);
`;
const Danger = styled(Button)`
  background:#e5484d; box-shadow:0 4px 12px rgba(229,72,77,.25);
`;
const Avatar = styled.div`
  width:80px; height:80px; border-radius:50%; overflow:hidden; background:#e6ecf5;
  img{ width:100%; height:100%; object-fit:cover; display:block; }
`;
const ToggleWrap = styled.div`
  display:flex; align-items:center; gap:.6rem;
`;
const Toggle = styled.button`
  width:44px; height:26px; border-radius:999px; border:1px solid #cfe0ff;
  background:${p => p.on ? "#3a79ed" : "#e7efff"}; position:relative; cursor:pointer;
  &:after{
    content:""; width:20px; height:20px; border-radius:50%; background:#fff;
    position:absolute; top:2px; left:${p => p.on ? "22px" : "2px"}; transition:left .15s ease;
    box-shadow:0 1px 3px rgba(0,0,0,.15);
  }
  &:disabled{ opacity:.6; cursor:not-allowed; }
`;
const Helper = styled.p`
  margin:0; color:#5b6478; font-size:.9rem;
`;
const ErrorBox = styled.div`
  background:#ffe6e6; color:#7a1f1f; border:1px solid #ffb3b3;
  padding:.8rem 1rem; border-radius:.75rem; font-weight:600;
`;
const OkBox = styled.div`
  background:#e8faf0; color:#157347; border:1px solid #b8eed6;
  padding:.8rem 1rem; border-radius:.75rem; font-weight:600;
`;
const SoonTag = styled.div`
  background-color: rgb(205 221 251);
  color:#3a79ed; padding:5px 8px; font-size:12px; border-radius:7px; margin-left: .5rem;
`;
const DisabledVeil = styled.div`
  display:${p => p.show ? "block" : "none"};
  position:absolute; inset:0; border-radius:1rem;
  background: linear-gradient(0deg, rgba(255,255,255,.65), rgba(255,255,255,.65));
  pointer-events:auto;
`;

/* ======= Página ======= */
export default function ConfiguracoesUsuario() {
  const navigate = useNavigate();
  const fileRef = useRef(null);

  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const [user, setUser] = useState(null);
  const [displayName, setDisplayName] = useState("");
  const [email, setEmail] = useState("");
  const [foto, setFoto] = useState("");

  // preferencias (Firestore: users/{uid})
  const [phone, setPhone] = useState("");
  const [notifEmail, setNotifEmail] = useState(true);
  const [notifWhats, setNotifWhats] = useState(false);

  const [msgOk, setMsgOk] = useState("");
  const [msgErr, setMsgErr] = useState("");

  const { success } = useToast();

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (u) => {
      if (!u) { navigate("/login"); return; }

      try { await reload(u); } catch (_) { }

      setUser(u);
      setDisplayName(u.displayName || "");
      setEmail(u.email || "");

      // tenta foto na ordem: Auth.photoURL -> users/{uid}.foto -> barbearia/{id}/barbeiros/{uid}.foto
      let photo = u.photoURL || "";

      try {
        const snap = await getDoc(doc(db, "users", u.uid));
        if (snap.exists()) {
          const d = snap.data();
          setPhone(d.phone || "");
          setNotifEmail(d?.notifications?.email ?? true);
          setNotifWhats(d?.notifications?.whatsapp ?? false);
          if (!photo && d?.foto) photo = d.foto;
        }
      } catch (e) {
        console.error(e);
      }

      if (!photo) {
        try {
          const idBarbearia = await resolveBarbeariaId(db, u.uid);
          if (idBarbearia) {
            const bSnap = await getDoc(doc(db, "barbearia", idBarbearia, "barbeiros", u.uid));
            if (bSnap.exists() && bSnap.data()?.foto) {
              photo = bSnap.data().foto;
            }
          }
        } catch (e) {
          console.error(e);
        }
      }

      // 🔥 se não tiver nenhuma foto, aplica a padrão
      const fotoPadrao =
        "https://firebasestorage.googleapis.com/v0/b/bigode-app-fade.firebasestorage.app/o/padrao-sistema%2Fbarbeiro.webp?alt=media&token=732c1f69-8182-414d-8237-ac0f509bf0fd";

      setFoto(photo || fotoPadrao);
      setLoading(false);
    });

    return () => unsub();
  }, [navigate]);

  // Tenta resolver o id da barbearia do usuário atual
  async function resolveBarbeariaId(db, uid) {
    try {
      const uSnap = await getDoc(doc(db, "users", uid));
      const b = uSnap.exists() ? uSnap.data()?.barbeariaId : null;
      if (b) return b;
    } catch (_) { }

    try {
      const qOwner = query(collection(db, "barbearia"), where("criadorUid", "==", uid), limit(1));
      const ownerSnap = await getDocs(qOwner);
      if (!ownerSnap.empty) return ownerSnap.docs[0].id;
    } catch (_) { }

    try {
      const qBarb = query(collectionGroup(db, "barbeiros"), where(documentId(), "==", uid), limit(1));
      const barbSnap = await getDocs(qBarb);
      if (!barbSnap.empty) {
        const ref = barbSnap.docs[0].ref;
        const barbeariaRef = ref.parent.parent;
        if (barbeariaRef) return barbeariaRef.id;
      }
    } catch (_) { }

    return null;
  }

  function toast(ok, msg) {
    if (ok) { success(msg) }
    else { alert(msg) }
  }

  async function salvarPerfil() {
    if (!user) return;
    setBusy(true);
    try {
      await updateProfile(user, {
        displayName: displayName || null,
        photoURL: foto || null,
      });
      await reload(user);

      // Salva preferências gerais do usuário (sem depender de NOTIFS_ENABLED)
      await setDoc(
        doc(db, "users", user.uid),
        {
          nome: displayName || "",
          foto: foto || null,
          phone,
          // mesmo que as notificações estejam desativadas na UI,
          // manteremos o objeto para compatibilidade futura:
          notifications: { email: notifEmail, whatsapp: notifWhats },
          updatedAt: new Date().toISOString(),
        },
        { merge: true }
      );

      const idBarbearia = await resolveBarbeariaId(db, user.uid);
      if (idBarbearia) {
        await setDoc(
          doc(db, "barbearia", idBarbearia, "barbeiros", user.uid),
          {
            nome: displayName || "",
            foto: foto || null,
            updatedAt: new Date().toISOString(),
          },
          { merge: true }
        );
      } else {
        console.warn("Usuário sem barbearia vinculada; não atualizei barbearia/.../barbeiros.");
      }

      toast(true, "Perfil atualizado com sucesso.");
      window.dispatchEvent(new CustomEvent("bigode:profile-updated", {
        detail: { uid: user.uid, displayName, foto }
      }));
    } catch (e) {
      console.error(e);
      toast(false, e?.message || "Falha ao salvar perfil.");
    } finally {
      setBusy(false);
    }
  }

  async function trocarSenha() {
    const u = auth.currentUser;
    const targetEmail = u?.email?.trim();
    if (!targetEmail) {
      toast(false, "Sua conta não tem um e-mail cadastrado.");
      return;
    }

    setBusy(true);
    try {
      await reload(u);
      const methods = await fetchSignInMethodsForEmail(auth, targetEmail);

      const actionCodeSettings = {
        url: `${window.location.origin}/login?reset=1`,
        handleCodeInApp: false,
      };
      await sendPasswordResetEmail(auth, targetEmail, actionCodeSettings);
      toast(true, `Enviamos o link para ${targetEmail}. Veja também o spam.`);

      if (!methods.includes("password")) {
        console.warn("[Reset enviado] Mas signInMethods não lista 'password':", methods);
      }
    } catch (e) {
      console.error("Erro reset senha:", e);
      const code = e?.code || "";

      if (code === "auth/user-not-found") {
        toast(false, "Não encontramos esse e-mail neste projeto Firebase.");
      } else if (code === "auth/invalid-email") {
        toast(false, "E-mail inválido.");
      } else if (code === "auth/unauthorized-continue-uri") {
        toast(false, "A URL de continuação não está em Domínios autorizados do Firebase Authentication.");
      } else if (code === "auth/missing-continue-uri") {
        toast(false, "Faltou configurar a URL de continuação (actionCodeSettings.url).");
      } else if (code === "auth/too-many-requests") {
        toast(false, "Muitas tentativas. Aguarde alguns minutos e tente novamente.");
      } else {
        try {
          const methods = await fetchSignInMethodsForEmail(auth, targetEmail);
          if (!methods.includes("password")) {
            toast(false, "Sua conta parece não estar vinculada ao provedor 'senha'. Posso ativar uma senha para este e-mail na sua conta logada.");
          } else {
            toast(false, e?.message || "Não foi possível enviar o e-mail de redefinição.");
          }
        } catch {
          toast(false, e?.message || "Não foi possível enviar o e-mail de redefinição.");
        }
      }
    } finally {
      setBusy(false);
    }
  }

  async function handleUploadPhoto(file) {
    if (!file || !user) return;
    try {
      setBusy(true);

      const idBarbearia = await resolveBarbeariaId(db, user.uid);
      if (!idBarbearia) {
        throw new Error("Não foi possível identificar sua barbearia para salvar a foto.");
      }

      const ext = (file.name?.split(".").pop() || "jpg").toLowerCase();
      const objectPath = `${idBarbearia}/barbeiros/${user.uid}/avatar_${Date.now()}.${ext}`;

      const r = ref(storage, objectPath);
      await uploadBytes(r, file);
      const url = await getDownloadURL(r);

      setFoto(url);

      await setDoc(
        doc(db, "barbearia", idBarbearia, "barbeiros", user.uid),
        { foto: url, updatedAt: new Date().toISOString() },
        { merge: true }
      );
    } catch (e) {
      console.error(e);
      toast(false, e?.message || "Falha ao enviar a foto.");
    } finally {
      setBusy(false);
    }
  }

  async function excluirConta() {
    if (!user) return;
    const confirma = window.confirm("Tem certeza que deseja excluir sua conta? Essa ação é permanente.");
    if (!confirma) return;
    try {
      setBusy(true);
      await deleteUser(user);
      toast(true, "Conta excluída.");
      navigate("/login");
    } catch (e) {
      console.error(e);
      if (e?.code === "auth/requires-recent-login") {
        toast(false, "Por segurança, faça login novamente e tente excluir de novo.");
      } else {
        toast(false, e?.message || "Não foi possível excluir a conta.");
      }
    } finally {
      setBusy(false);
    }
  }

  if (loading) return <LoadingComLayout />;

  return (
    <>
      <Main>

        {msgErr && <ErrorBox>{msgErr}</ErrorBox>}
        {msgOk && <OkBox>{msgOk}</OkBox>}

        <Grid>
          {/* coluna esquerda */}
          <div style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
            <Card>
              <CardHeader><LuUser /> Perfil</CardHeader>
              <CardBody>
                <Row>
                  <Label>Foto</Label>
                  <div style={{ display: "flex", alignItems: "center", gap: ".9rem" }}>
                    <Avatar>{foto ? <img src={foto} alt="avatar" /> : null}</Avatar>
                    <Button onClick={() => fileRef.current?.click()} disabled={busy}>
                      <LuCamera /> Trocar foto
                    </Button>
                    <input
                      ref={fileRef}
                      type="file" accept="image/*" style={{ display: "none" }}
                      onChange={(e) => handleUploadPhoto(e.target.files?.[0])}
                    />
                  </div>
                </Row>

                <Row>
                  <Label>Nome</Label>
                  <Input
                    value={displayName} onChange={e => setDisplayName(e.target.value)}
                    placeholder="Seu nome"
                  />
                </Row>

                <Row>
                  <Label>E-mail</Label>
                  <div style={{ display: "flex", alignItems: "center", gap: ".6rem" }}>
                    <LuMail />{email || "—"}
                  </div>
                </Row>

                <div style={{ display: "flex", gap: ".6rem", flexWrap: "wrap", marginTop: 20 }}>
                  <Button onClick={salvarPerfil} disabled={busy}>Salvar</Button>
                  <Button onClick={() => navigate("/assinatura")}>
                    <LuExternalLink /> Gerenciar assinaturas
                  </Button>
                </div>
              </CardBody>
            </Card>

            {/* ===== Notificações (DESATIVADO) ===== */}
            <Card style={!NOTIFS_ENABLED ? { opacity: .6 } : undefined} aria-disabled={!NOTIFS_ENABLED}>
              <CardHeader>
                <LuBell /> Notificações
                <SoonTag>Em Breve</SoonTag>
              </CardHeader>
              <DisabledVeil show={!NOTIFS_ENABLED} aria-hidden="true" />
              <CardBody>
                {/* fieldset desabilita todos os inputs/botões internos */}
                <fieldset disabled={!NOTIFS_ENABLED} style={{ border: 0, padding: 0, margin: 0, display:'flex', flexDirection:'column', gap:'0.5rem' }}>
                  <Row>
                    <Label>Telefone (WhatsApp)</Label>
                    <Input
                      value={phone} onChange={e => setPhone(e.target.value)}
                      placeholder="Ex.: 11 99999-9999"
                    />
                  </Row>
                  <Row>
                    <Label>Receber por e-mail</Label>
                    <ToggleWrap>
                      <Toggle onClick={() => setNotifEmail(v => !v)} on={notifEmail} disabled={!NOTIFS_ENABLED} />
                      <Helper>Lembretes, recibos e novidades.</Helper>
                    </ToggleWrap>
                  </Row>
                  <Row>
                    <Label>Receber por WhatsApp</Label>
                    <ToggleWrap>
                      <Toggle onClick={() => setNotifWhats(v => !v)} on={notifWhats} disabled={!NOTIFS_ENABLED} />
                      <Helper>Confirmações e lembretes de agendamentos.</Helper>
                    </ToggleWrap>
                  </Row>
                  <Button onClick={salvarPerfil} disabled={!NOTIFS_ENABLED || busy} title="Em breve">
                    Salvar preferências
                  </Button>
                </fieldset>
              </CardBody>
            </Card>
          </div>

          {/* coluna direita */}
          <div style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
            <Card>
              <CardHeader><LuShield /> Segurança</CardHeader>
              <CardBody>
                <Helper>
                  Use uma senha forte. Em breve: autenticação em 2 etapas (2FA).
                </Helper>
                <Secondary onClick={trocarSenha} disabled={busy}>
                  <LuKeyRound /> Enviar link para redefinir senha
                </Secondary>
              </CardBody>
            </Card>

            <Card>
              <CardHeader><LuTrash2 /> Zona de perigo</CardHeader>
              <CardBody>
                <Helper>Excluir sua conta é permanente e não pode ser desfeita.</Helper>
                <Danger onClick={excluirConta} disabled={busy}>Excluir conta</Danger>
              </CardBody>
            </Card>
          </div>
        </Grid>
      </Main>
    </>
  );
}
